import React, { useState } from 'react';
import { CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/Badge';
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-parchment last:border-0" data-id="element-1041">
      <button className="w-full flex items-center justify-between py-5 text-left group" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen} data-id="element-1042">
        <span className="font-serif text-base font-semibold text-ink group-hover:text-amber transition-colors pr-4" data-id="element-1043">
          {question}
        </span>
        {isOpen ? <ChevronUpIcon className="w-5 h-5 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1044" /> : <ChevronDownIcon className="w-5 h-5 text-ink-light flex-shrink-0" aria-hidden="true" data-id="element-1045" />}
      </button>
      {isOpen && <div className="pb-5" data-id="element-1046">
          <p className="font-sans text-sm text-ink-muted leading-relaxed" data-id="element-1047">
            {answer}
          </p>
        </div>}
    </div>;
}
export function PricingPage() {
  const [isAnnual, setIsAnnual] = useState(true);
  return <main className="w-full bg-cream" data-id="element-1048">
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-8 text-center" aria-labelledby="pricing-heading" data-id="element-1049">
        <div className="max-w-3xl mx-auto" data-id="element-1050">
          <Badge variant="amber" className="mb-6" data-id="element-1051">
            Pricing
          </Badge>
          <h1 id="pricing-heading" className="font-serif text-5xl md:text-6xl font-bold text-ink mb-6 leading-tight" data-id="element-1052">
            Simple, honest pricing.
          </h1>
          <p className="font-sans text-lg text-ink-muted leading-relaxed mb-10" data-id="element-1053">
            The free plan is genuinely free, forever. No credit card required,
            no tricks, no dark patterns. Upgrade when you need more.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-4 bg-parchment rounded-full p-1.5 border border-parchment" data-id="element-1054">
            <button className={`font-sans text-sm px-5 py-2 rounded-full transition-all ${!isAnnual ? 'bg-white text-ink shadow-sm font-medium' : 'text-ink-muted hover:text-ink'}`} onClick={() => setIsAnnual(false)} aria-pressed={!isAnnual} data-id="element-1055">
              Monthly
            </button>
            <button className={`font-sans text-sm px-5 py-2 rounded-full transition-all flex items-center gap-2 ${isAnnual ? 'bg-white text-ink shadow-sm font-medium' : 'text-ink-muted hover:text-ink'}`} onClick={() => setIsAnnual(true)} aria-pressed={isAnnual} data-id="element-1056">
              Annual
              <span className="bg-amber-light text-warm-brown text-xs font-medium px-2 py-0.5 rounded-full border border-amber/20" data-id="element-1057">
                Save 20%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-24 px-6 lg:px-8" aria-label="Pricing tiers" data-id="element-1058">
        <div className="max-w-6xl mx-auto" data-id="element-1059">
          <div className="grid md:grid-cols-3 gap-8 items-start" data-id="element-1060">
            {/* Free */}
            <div className="bg-white rounded-2xl p-8 border border-parchment shadow-sm" data-id="element-1061">
              <div className="mb-8" data-id="element-1062">
                <h2 className="font-serif text-2xl font-bold text-ink mb-1" data-id="element-1063">
                  Free
                </h2>
                <p className="font-sans text-sm text-ink-muted mb-4" data-id="element-1064">
                  For writers just getting started
                </p>
                <div className="flex items-baseline gap-1" data-id="element-1065">
                  <span className="font-serif text-5xl font-bold text-ink" data-id="element-1066">
                    $0
                  </span>
                  <span className="font-sans text-sm text-ink-light" data-id="element-1067">
                    /month
                  </span>
                </div>
                <p className="font-sans text-xs text-ink-light mt-1" data-id="element-1068">
                  Free forever. No card required.
                </p>
              </div>

              <Link to="/waitlist" className="block w-full text-center font-sans text-sm font-medium border border-ink/20 text-ink hover:bg-parchment py-3 rounded-full transition-colors mb-8" data-id="element-1069">
                Get Started Free
              </Link>

              <div className="space-y-3" data-id="element-1070">
                <div className="font-sans text-xs text-ink-light uppercase tracking-wide mb-3" data-id="element-1071">
                  What's included
                </div>
                <div className="flex items-center gap-3" data-id="element-1072">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1073" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1074">
                    1 project
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1075">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1076" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1077">
                    10 characters
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1078">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1079" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1080">
                    20 scenes
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1081">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1082" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1083">
                    5 locations
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1084">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1085" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1086">
                    Basic world-building
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1087">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1088" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1089">
                    Community support
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1090">
                  <XIcon className="w-4 h-4 text-ink-light flex-shrink-0" aria-hidden="true" data-id="element-1091" />
                  <span className="font-sans text-sm text-ink-light" data-id="element-1092">
                    AI features
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1093">
                  <XIcon className="w-4 h-4 text-ink-light flex-shrink-0" aria-hidden="true" data-id="element-1094" />
                  <span className="font-sans text-sm text-ink-light" data-id="element-1095">
                    Export (PDF/Fountain)
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1096">
                  <XIcon className="w-4 h-4 text-ink-light flex-shrink-0" aria-hidden="true" data-id="element-1097" />
                  <span className="font-sans text-sm text-ink-light" data-id="element-1098">
                    Collaboration (paid plans)
                  </span>
                </div>
              </div>
            </div>

            {/* Pro */}
            <div className="bg-navy rounded-2xl p-8 border border-navy shadow-xl relative" data-id="element-1099">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2" data-id="element-1100">
                <span className="bg-amber text-white font-sans text-xs font-semibold px-5 py-1.5 rounded-full shadow-md" data-id="element-1101">
                  Most Popular
                </span>
              </div>
              <div className="mb-8" data-id="element-1102">
                <h2 className="font-serif text-2xl font-bold text-white mb-1" data-id="element-1103">
                  Pro
                </h2>
                <p className="font-sans text-sm text-white/60 mb-4" data-id="element-1104">
                  For serious writers and full projects
                </p>
                <div className="flex items-baseline gap-1" data-id="element-1105">
                  <span className="font-serif text-5xl font-bold text-white" data-id="element-1106">
                    {isAnnual ? '$12' : '$15'}
                  </span>
                  <span className="font-sans text-sm text-white/50" data-id="element-1107">
                    /month
                  </span>
                </div>
                <p className="font-sans text-xs text-white/40 mt-1" data-id="element-1108">
                  {isAnnual ? 'Billed annually ($144/year)' : 'Billed monthly'}
                </p>
              </div>

              <Link to="/waitlist" className="block w-full text-center font-sans text-sm font-medium bg-amber hover:bg-amber-dark text-white py-3 rounded-full transition-colors mb-8" data-id="element-1109">
                Start Free Trial
              </Link>

              <div className="space-y-3" data-id="element-1110">
                <div className="font-sans text-xs text-white/40 uppercase tracking-wide mb-3" data-id="element-1111">
                  Everything in Free, plus
                </div>
                <div className="flex items-center gap-3" data-id="element-1112">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1113" />
                  <span className="font-sans text-sm text-white/80" data-id="element-1114">
                    Unlimited projects
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1115">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1116" />
                  <span className="font-sans text-sm text-white/80" data-id="element-1117">
                    Unlimited characters
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1118">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1119" />
                  <span className="font-sans text-sm text-white/80" data-id="element-1120">
                    Unlimited scenes
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1121">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1122" />
                  <span className="font-sans text-sm text-white/80" data-id="element-1123">
                    Unlimited locations
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1124">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1125" />
                  <span className="font-sans text-sm text-white/80" data-id="element-1126">
                    Full world-building suite
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1127">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1128" />
                  <span className="font-sans text-sm text-white/80" data-id="element-1129">
                    AI features
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1130">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1131" />
                  <span className="font-sans text-sm text-white/80" data-id="element-1132">
                    PDF & Fountain export
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1133">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1134" />
                  <span className="font-sans text-sm text-white/80" data-id="element-1135">
                    Priority support
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1136">
                  <XIcon className="w-4 h-4 text-white/20 flex-shrink-0" aria-hidden="true" data-id="element-1137" />
                  <span className="font-sans text-sm text-white/30" data-id="element-1138">
                    Collaboration (Team plan)
                  </span>
                </div>
              </div>
            </div>

            {/* Team */}
            <div className="bg-white rounded-2xl p-8 border border-parchment shadow-sm" data-id="element-1139">
              <div className="mb-8" data-id="element-1140">
                <h2 className="font-serif text-2xl font-bold text-ink mb-1" data-id="element-1141">
                  Team
                </h2>
                <p className="font-sans text-sm text-ink-muted mb-4" data-id="element-1142">
                  For writing rooms and collaborators
                </p>
                <div className="flex items-baseline gap-1" data-id="element-1143">
                  <span className="font-serif text-5xl font-bold text-ink" data-id="element-1144">
                    $29
                  </span>
                  <span className="font-sans text-sm text-ink-light" data-id="element-1145">
                    /seat/mo
                  </span>
                </div>
                <p className="font-sans text-xs text-ink-light mt-1" data-id="element-1146">
                  Min. 2 seats · Billed annually
                </p>
              </div>

              <a href="#" className="block w-full text-center font-sans text-sm font-medium border border-ink/20 text-ink hover:bg-parchment py-3 rounded-full transition-colors mb-8" data-id="element-1147">
                Contact Sales
              </a>

              <div className="space-y-3" data-id="element-1148">
                <div className="font-sans text-xs text-ink-light uppercase tracking-wide mb-3" data-id="element-1149">
                  Everything in Pro, plus
                </div>
                <div className="flex items-center gap-3" data-id="element-1150">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1151" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1152">
                    Real-time collaboration
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1153">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1154" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1155">
                    Feedback & @mentions
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1156">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1157" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1158">
                    View & Edit permissions
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1159">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1160" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1161">SSO</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1162">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1163" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1164">
                    Dedicated support
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-1165">
                  <CheckIcon className="w-4 h-4 text-amber flex-shrink-0" aria-hidden="true" data-id="element-1166" />
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1167">
                    Admin controls
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="bg-parchment py-24 px-6 lg:px-8" aria-labelledby="comparison-heading" data-id="element-1168">
        <div className="max-w-4xl mx-auto" data-id="element-1169">
          <h2 id="comparison-heading" className="font-serif text-3xl font-bold text-ink mb-12 text-center" data-id="element-1170">
            Compare plans
          </h2>

          <div className="bg-white rounded-2xl border border-parchment overflow-hidden shadow-sm" data-id="element-1171">
            <div className="grid grid-cols-4 bg-navy text-white" data-id="element-1172">
              <div className="p-5 font-sans text-sm font-medium text-white/60" data-id="element-1173">
                Feature
              </div>
              <div className="p-5 text-center font-serif text-base font-semibold" data-id="element-1174">
                Free
              </div>
              <div className="p-5 text-center font-serif text-base font-semibold text-amber" data-id="element-1175">
                Pro
              </div>
              <div className="p-5 text-center font-serif text-base font-semibold" data-id="element-1176">
                Team
              </div>
            </div>

            {[{
            feature: 'Projects',
            free: '1',
            pro: 'Unlimited',
            team: 'Unlimited'
          }, {
            feature: 'Characters',
            free: '10',
            pro: 'Unlimited',
            team: 'Unlimited'
          }, {
            feature: 'Scenes',
            free: '20',
            pro: 'Unlimited',
            team: 'Unlimited'
          }, {
            feature: 'Locations',
            free: '5',
            pro: 'Unlimited',
            team: 'Unlimited'
          }, {
            feature: 'World-building',
            free: 'Basic',
            pro: 'Full suite',
            team: 'Full suite'
          }, {
            feature: 'AI features',
            free: false,
            pro: true,
            team: true
          }, {
            feature: 'Export (PDF/Fountain)',
            free: false,
            pro: true,
            team: true
          }, {
            feature: 'Collaboration',
            free: false,
            pro: false,
            team: true,
            note: 'Requires a paid plan'
          }, {
            feature: 'Feedback & @mentions',
            free: false,
            pro: false,
            team: true
          }, {
            feature: 'SSO',
            free: false,
            pro: false,
            team: true
          }, {
            feature: 'Support',
            free: 'Community',
            pro: 'Priority',
            team: 'Dedicated'
          }].map((row, i) => <div key={row.feature} className={`grid grid-cols-4 border-t border-parchment ${i % 2 === 0 ? 'bg-white' : 'bg-cream/50'}`} data-id="element-1177">
                <div className="p-4" data-id="element-1178">
                  <span className="font-sans text-sm text-ink-muted" data-id="element-1179">
                    {row.feature}
                  </span>
                  {row.note && <span className="block font-sans text-xs text-ink-light mt-0.5 italic" data-id="element-1180">
                      {row.note}
                    </span>}
                </div>
                <div className="p-4 text-center font-sans text-sm text-ink-muted" data-id="element-1181">
                  {typeof row.free === 'boolean' ? row.free ? <CheckIcon className="w-4 h-4 text-amber mx-auto" aria-label="Included" data-id="element-1182" /> : <XIcon className="w-4 h-4 text-ink-light mx-auto" aria-label="Not included" data-id="element-1183" /> : row.free}
                </div>
                <div className="p-4 text-center font-sans text-sm text-ink-muted" data-id="element-1184">
                  {typeof row.pro === 'boolean' ? row.pro ? <CheckIcon className="w-4 h-4 text-amber mx-auto" aria-label="Included" data-id="element-1185" /> : <XIcon className="w-4 h-4 text-ink-light mx-auto" aria-label="Not included" data-id="element-1186" /> : row.pro}
                </div>
                <div className="p-4 text-center font-sans text-sm text-ink-muted" data-id="element-1187">
                  {typeof row.team === 'boolean' ? row.team ? <CheckIcon className="w-4 h-4 text-amber mx-auto" aria-label="Included" data-id="element-1188" /> : <XIcon className="w-4 h-4 text-ink-light mx-auto" aria-label="Not included" data-id="element-1189" /> : row.team}
                </div>
              </div>)}
          </div>

          <p className="font-sans text-xs text-ink-light text-center mt-4 italic" data-id="element-1190">
            * Collaboration features require a paid plan (Pro or Team).
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-cream py-24 px-6 lg:px-8" aria-labelledby="faq-heading" data-id="element-1191">
        <div className="max-w-3xl mx-auto" data-id="element-1192">
          <h2 id="faq-heading" className="font-serif text-3xl font-bold text-ink mb-12 text-center" data-id="element-1193">
            Frequently asked questions
          </h2>

          <div className="bg-white rounded-2xl border border-parchment shadow-sm p-8" data-id="element-1194">
            <FAQItem question="What happens when I hit the free tier limits?" answer="When you reach the limits on the free plan (1 project, 10 characters, 20 scenes, 5 locations), you'll be prompted to upgrade to a paid plan. Your existing data is always safe — you can still view and read everything, but you won't be able to add new entries until you upgrade or remove existing ones." data-id="element-1195" />
            <FAQItem question="Can I export my data?" answer="Yes — Pro and Team plans include export to PDF and Fountain format. You can also export your raw data as JSON at any time on any plan, so your work is never locked in. We believe your story belongs to you." data-id="element-1196" />
            <FAQItem question="Does it work with Final Draft, Arc Studio Pro, or other editors?" answer="Slate & Page is designed to work alongside any screenplay editor — Final Draft, Arc Studio Pro, Highland, Fade In, or even a plain text editor. It's a development layer, not a replacement. Write your script wherever you're comfortable; use Slate & Page for everything around it." data-id="element-1197" />
            <FAQItem question="Is my work private and secure?" answer="Yes. Your projects are private by default — only you can see them unless you explicitly share them. We use industry-standard encryption for data at rest and in transit. We will never sell your data or use your story content to train AI models." data-id="element-1198" />
            <FAQItem question="Do I need a paid plan to collaborate with others?" answer="Yes — collaboration features (real-time editing, feedback, @mentions, and sharing with edit permissions) require a paid plan. The Team plan is designed for writing rooms and co-writers, while Pro includes basic sharing. The free plan is for solo writers only." data-id="element-1199" />
            <FAQItem question="Can I cancel anytime?" answer="Yes, absolutely. You can cancel your subscription at any time from your account settings. If you cancel a paid plan, you'll retain access until the end of your billing period, then revert to the free plan. No cancellation fees, no hard feelings." data-id="element-1200" />
            <FAQItem question="What's on the roadmap?" answer="We're actively working on enhanced collaboration features, AI-powered continuity checking, expanded export options (Final Draft XML, Celtx), a mobile app, and deeper integration with screenplay editors. Early Access users get first access to new features and direct input on the roadmap." data-id="element-1201" />
            <FAQItem question="Do you offer discounts for students?" answer="Yes — we offer a 50% discount for students and educators with a valid .edu email address. Contact us at hello@slateandpage.com with proof of enrollment and we'll set you up." data-id="element-1202" />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-parchment py-24 px-6 lg:px-8" aria-labelledby="pricing-cta-heading" data-id="element-1203">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1204">
          <h2 id="pricing-cta-heading" className="font-serif text-4xl font-bold text-ink mb-6" data-id="element-1205">
            Start free. No card required.
          </h2>
          <p className="font-sans text-lg text-ink-muted mb-10" data-id="element-1206">
            The free plan is a real plan, not a trial. Build your first story
            bible and upgrade when you're ready.
          </p>
          <Link to="/waitlist" className="inline-block font-sans font-medium text-base bg-amber hover:bg-amber-dark text-white px-10 py-4 rounded-full transition-colors shadow-md" data-id="element-1207">
            Join the Waitlist
          </Link>
        </div>
      </section>
    </main>;
}